// src/pages/IncomePotentialQuiz.tsx
import React, { useState } from "react";
import { ChevronRight, Target, Award, CheckCircle } from "lucide-react";
import ResultsScreen from "./ResultScreen";

// Types
interface QuizData {
  skill: string;
  monthlyGoal: string;
  firstName: string;
  email: string;
  whatsapp: string;
}

interface TierResult {
  price: number;
  units: number;
}

interface Result {
  tiers: TierResult[];
  totalRevenue: string;
  timeline: string;
}

// interface Product {
//   name: string;
//   price: string;
//   description: string;
//   monthlySales: string;
// }

interface Question {
  id: keyof Pick<QuizData, "skill" | "monthlyGoal">;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  options: string[];
}

// Questions
const QUESTIONS: Question[] = [
  {
    id: "skill",
    title: "What's your area of skill or expertise?",
    subtitle: "Choose the field you know best",
    icon: <Award className="w-8 h-8" />,
    options: [
      "Digital Marketing & Social Media",
      "Business & Entrepreneurship",
      "Finance & Investment",
      "Technology & Programming",
      "Health & Fitness",
      "Personal Development",
      "Content Creation & Writing",
      "Photography & Design",
      "Cooking & Food",
      "Fashion & Beauty",
      "Real Estate",
      "Education & Training",
      "Sales & Customer Service",
      "Crafts & DIY",
    ],
  },
  {
    id: "monthlyGoal",
    title: "What's your monthly income goal?",
    subtitle: "How much do you want to earn per month?",
    icon: <Target className="w-8 h-8" />,
    options: [
      "₦500K per month",
      "₦1M per month",
      "₦2M per month",
      "₦3M per month",
      "₦5M per month",
      "₦10M per month",
    ],
  },
];

// Business Logic: calculation
const parseGoalAmount = (goalString: string): number => {
  const match = goalString?.match(/₦(\d+(?:\.\d+)?)([KM]?)/);
  if (!match) return 500000;

  const amount = parseFloat(match[1]);
  const unit = match[2];

  if (unit === "M") return amount * 1_000_000;
  if (unit === "K") return amount * 1_000;
  return amount;
};

const calculateResult = (data: QuizData): Result => {
  // defensive defaults
  const monthlyGoal = data.monthlyGoal || "₦500K per month";
  const goalAmount = parseGoalAmount(monthlyGoal);

  const prices = [5000, 25000, 100000]; // price tiers
  const share = goalAmount / prices.length;
  const tiers = prices.map((price) => ({
    price,
    units: Math.max(0, Math.ceil(share / price)),
  }));

  return {
    tiers,
    totalRevenue: monthlyGoal,
    timeline: "30 days",
  };
};

// (Optional) Product strategy generator — unused in UI but kept for future use
// const generateProducts = (skill: string, goalAmount: number): Product[] => {
//   const productStrategies: Record<string, (goal: number) => Product[]> = {
//     "Digital Marketing & Social Media": (goal) => [
//       {
//         name: "Social Media Templates Bundle",
//         price: `₦${Math.round(goal * 0.02).toLocaleString()}`,
//         description: "Ready-to-use templates, captions, and graphics",
//         monthlySales: `${Math.ceil((goal * 0.3) / (goal * 0.02))} sales/month`,
//       },
//       {
//         name: "Digital Marketing Masterclass",
//         price: `₦${Math.round(goal * 0.08).toLocaleString()}`,
//         description: "Complete video course on marketing strategies",
//         monthlySales: `${Math.ceil((goal * 0.4) / (goal * 0.08))} sales/month`,
//       },
//       {
//         name: "Done-For-You Marketing Service",
//         price: `₦${Math.round(goal * 0.25).toLocaleString()}`,
//         description: "Monthly marketing management for businesses",
//         monthlySales: `${Math.ceil(
//           (goal * 0.3) / (goal * 0.25)
//         )} clients/month`,
//       },
//     ],

//     "Business & Entrepreneurship": (goal) => [
//       {
//         name: "Business Startup Kit",
//         price: `₦${Math.round(goal * 0.025).toLocaleString()}`,
//         description: "Templates, guides, and business plan resources",
//         monthlySales: `${Math.ceil(
//           (goal * 0.25) / (goal * 0.025)
//         )} sales/month`,
//       },
//       {
//         name: "Business Growth Program",
//         price: `₦${Math.round(goal * 0.12).toLocaleString()}`,
//         description: "8-week intensive business building course",
//         monthlySales: `${Math.ceil(
//           (goal * 0.45) / (goal * 0.12)
//         )} enrollments/month`,
//       },
//       {
//         name: "1-on-1 Business Consulting",
//         price: `₦${Math.round(goal * 0.35).toLocaleString()}`,
//         description: "Personal business coaching and mentorship",
//         monthlySales: `${Math.ceil(
//           (goal * 0.3) / (goal * 0.35)
//         )} clients/month`,
//       },
//     ],

//     "Finance & Investment": (goal) => [
//       {
//         name: "Investment Beginner's Guide",
//         price: `₦${Math.round(goal * 0.02).toLocaleString()}`,
//         description: "Complete guide to investing in Nigeria",
//         monthlySales: `${Math.ceil((goal * 0.2) / (goal * 0.02))} sales/month`,
//       },
//       {
//         name: "Wealth Building Masterclass",
//         price: `₦${Math.round(goal * 0.15).toLocaleString()}`,
//         description: "Advanced investment strategies and portfolio building",
//         monthlySales: `${Math.ceil(
//           (goal * 0.5) / (goal * 0.15)
//         )} students/month`,
//       },
//       {
//         name: "Personal Finance Consulting",
//         price: `₦${Math.round(goal * 0.4).toLocaleString()}`,
//         description: "One-on-one financial planning sessions",
//         monthlySales: `${Math.ceil(
//           (goal * 0.3) / (goal * 0.4)
//         )} consultations/month`,
//       },
//     ],

//     "Health & Fitness": (goal) => [
//       {
//         name: "Workout Plans & Meal Guides",
//         price: `₦${Math.round(goal * 0.015).toLocaleString()}`,
//         description: "Customizable fitness and nutrition plans",
//         monthlySales: `${Math.ceil(
//           (goal * 0.25) / (goal * 0.015)
//         )} sales/month`,
//       },
//       {
//         name: "Fitness Transformation Course",
//         price: `₦${Math.round(goal * 0.08).toLocaleString()}`,
//         description: "12-week body transformation program",
//         monthlySales: `${Math.ceil(
//           (goal * 0.45) / (goal * 0.08)
//         )} participants/month`,
//       },
//       {
//         name: "Personal Training Service",
//         price: `₦${Math.round(goal * 0.2).toLocaleString()}`,
//         description: "Monthly personal training packages",
//         monthlySales: `${Math.ceil((goal * 0.3) / (goal * 0.2))} clients/month`,
//       },
//     ],
//   };

//   const defaultStrategy = (goal: number): Product[] => [
//     {
//       name: "Digital Resource Pack",
//       price: `₦${Math.round(goal * 0.02).toLocaleString()}`,
//       description: "Templates, guides, and tools in your expertise",
//       monthlySales: `${Math.ceil((goal * 0.3) / (goal * 0.02))} sales/month`,
//     },
//     {
//       name: "Complete Training Course",
//       price: `₦${Math.round(goal * 0.1).toLocaleString()}`,
//       description: "Comprehensive course teaching your skills",
//       monthlySales: `${Math.ceil((goal * 0.4) / (goal * 0.1))} students/month`,
//     },
//     {
//       name: "Premium Service Package",
//       price: `₦${Math.round(goal * 0.3).toLocaleString()}`,
//       description: "Done-for-you service in your area of expertise",
//       monthlySales: `${Math.ceil((goal * 0.3) / (goal * 0.3))} clients/month`,
//     },
//   ];

//   const strategy = productStrategies[skill] || defaultStrategy;
//   return strategy(goalAmount);
// };

// Components: Progress & QuestionCard
const ProgressBar: React.FC<{ current: number; total: number }> = ({
  current,
  total,
}) => (
  <div className="mb-8">
    <div className="flex justify-between text-sm text-gray-500 mb-2">
      <span>
        Step {current + 1} of {total}
      </span>
      <span>{Math.round(((current + 1) / total) * 100)}%</span>
    </div>
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
        style={{ width: `${((current + 1) / total) * 100}%` }}
      />
    </div>
  </div>
);

const QuestionCard: React.FC<{
  question: Question;
  value: string;
  onSelect: (value: string) => void;
}> = ({ question, value, onSelect }) => (
  <div className="mb-8">
    <div className="flex items-center gap-4 mb-6">
      <div className="text-blue-600">{question.icon}</div>
      <div>
        <h2 className="text-2xl font-bold text-gray-800">{question.title}</h2>
        <p className="text-gray-600">{question.subtitle}</p>
      </div>
    </div>

    <div className="space-y-3">
      {question.options.map((option) => (
        <button
          key={option}
          onClick={() => onSelect(option)}
          className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
            value === option
              ? "border-blue-500 bg-blue-50 text-blue-700"
              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  </div>
);

// Local SuccessScreen (shown after ResultsScreen calls parent onSubmit)
const SuccessScreen: React.FC<{ firstName: string }> = ({ firstName }) => (
  <div className="min-h-screen bg-gradient-to-br from-green-100 to-blue-100 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
      <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
      <h2 className="text-2xl font-bold text-gray-800 mb-4">
        Thank You, {firstName}! 📧
      </h2>
      <p className="text-gray-600 mb-6">
        Your complete action plan has been sent to your email and WhatsApp.
        Check your inbox for the step-by-step guide to create and sell your 3
        products.
      </p>
      <div className="bg-green-50 p-4 rounded-lg">
        <p className="text-sm text-green-700">
          💡 <strong>What's Next:</strong> Follow the action plan to create your
          first product within 7 days!
        </p>
      </div>
    </div>
  </div>
);

// Main Component
const IncomePotentialQuiz: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [quizData, setQuizData] = useState<QuizData>({
    skill: "",
    monthlyGoal: "",
    firstName: "",
    email: "",
    whatsapp: "",
  });
  const [result, setResult] = useState<Result | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleNext = () => {
    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      const calculatedResult = calculateResult(quizData);
      setResult(calculatedResult);
      setCurrentStep((s) => s + 1);
    }
  };

  // passed to ResultsScreen so it can update the parent UI after server success
  const handleSubmit = () => {
    setShowSuccess(true);
  };

  const updateQuizData = (field: keyof QuizData, value: string) => {
    setQuizData((prev) => ({ ...prev, [field]: value }));
  };

  // Show success screen (parent-level)
  if (showSuccess) {
    return <SuccessScreen firstName={quizData.firstName} />;
  }

  // Show results screen (final step)
  if (currentStep === QUESTIONS.length && result) {
    return (
      <ResultsScreen
        result={result}
        skill={quizData.skill}
        firstName={quizData.firstName}
        email={quizData.email}
        whatsapp={quizData.whatsapp}
        onFirstNameChange={(name) => updateQuizData("firstName", name)}
        onEmailChange={(email) => updateQuizData("email", email)}
        onWhatsappChange={(whatsapp) => updateQuizData("whatsapp", whatsapp)}
        onSubmit={handleSubmit}
      />
    );
  }

  // Render quiz steps
  if (currentStep < QUESTIONS.length) {
    const currentQuestion = QUESTIONS[currentStep];

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
          <ProgressBar current={currentStep} total={QUESTIONS.length} />

          <QuestionCard
            question={currentQuestion}
            value={quizData[currentQuestion.id]}
            onSelect={(value) => updateQuizData(currentQuestion.id, value)}
          />

          <button
            onClick={handleNext}
            disabled={!quizData[currentQuestion.id]}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {currentStep === QUESTIONS.length - 1
              ? "Show Me My 3 Products Strategy 🚀"
              : "Continue"}
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>

          <p className="text-center text-xs text-gray-500 mt-4">
            🔒 Your information is secure and will never be shared
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default IncomePotentialQuiz;
